import { get, set, unset } from 'lodash';
import React, { useCallback, useContext, useMemo } from 'react';
import { Calendar } from 'sr-types/lib/search/v1/graphql';
import { FORM_WIDGET_COMPONENTS } from '../../../form/formConstants';
import { FormContext, FormContextType } from '../../../form/FormContext';
import FormWidget from '../../../form/FormWidget';
import { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { H } from '../../../Layout';
import { SCHEDULE_FORM_WIDGET_FIELD_NAMES } from '../constants';
import { ScheduleFormWidgetValue, ScheduleHierarchyFormWidgetValue } from '../types';
import useScheduleFormWidgetContext from '../useScheduleFormWidgetContext';
import { mapCalendarDataToOptions } from '../utils/data-to-options-utils';
import { resolveStateFieldName } from '../utils/form-utils';

export interface ScheduleCalendarFormWidgetProps {
    name?: string;
    disabled?: boolean;
}

export default function ScheduleCalendarFormWidget({
    name: nameProp,
    disabled: disabledProp = false
}: ScheduleCalendarFormWidgetProps) {
    const t = useI18n();

    const { loading, error, disabled, calendarsData, getFormStateByFieldName } = useScheduleFormWidgetContext();

    const isCalendarDataReady = useMemo(
        () => !loading && calendarsData && calendarsData.length > 0,
        [calendarsData, loading]
    );

    const calendarOptions = useMemo(() => {
        if (!isCalendarDataReady) {
            return [];
        }

        const calendarOptions = mapCalendarDataToOptions(calendarsData);

        return calendarOptions;
    }, [calendarsData, isCalendarDataReady]);

    const name = resolveStateFieldName([nameProp, SCHEDULE_FORM_WIDGET_FIELD_NAMES.CALENDAR]);

    const value = useMemo(() => {
        if (!isCalendarDataReady) {
            return '';
        }

        const maybeSelectedCalendarId = (getFormStateByFieldName(name) as Calendar)?.id;

        if (!maybeSelectedCalendarId) {
            return '';
        }

        const foundMatchingCalendarData = Boolean(calendarsData.find((c) => c.id === maybeSelectedCalendarId));

        if (!foundMatchingCalendarData) {
            console.warn(`Selected calendar id "${maybeSelectedCalendarId}" not found in available calendars data.`);
            return '';
        }

        return maybeSelectedCalendarId;
    }, [calendarsData, getFormStateByFieldName, isCalendarDataReady, name]);

    const { state: previousState, setState } =
        useContext<FormContextType<ScheduleFormWidgetValue | ScheduleHierarchyFormWidgetValue>>(FormContext);

    const onChange = useCallback(
        (_name: string, value: string) => {
            const previousValue = get(previousState, name);

            const updatedState = {
                ...previousState
            };

            const newValue = calendarsData?.find((c) => c.id === value);

            if (!newValue) {
                if (previousValue) {
                    unset(updatedState, name);
                    setState(updatedState);
                }
            } else {
                if (previousValue !== newValue) {
                    set(updatedState, name, newValue);
                    setState(updatedState);
                }
            }
        },
        [calendarsData, name, previousState, setState]
    );

    const isWidgetDisabled = disabled || disabledProp || loading;

    const widgetErrors = get(error, name);

    return (
        <H sx={{ alignItems: 'center', gap: 1 }}>
            <Icons.Calendar color={isWidgetDisabled ? 'disabled' : 'action'} />
            <FormWidget
                component={FORM_WIDGET_COMPONENTS.autocomplete}
                value={value}
                onChange={onChange}
                label={t('scheduleFormWidget.calendar.label')}
                options={calendarOptions}
                disabled={isWidgetDisabled}
                hasErrors={Boolean(widgetErrors)}
                errors={widgetErrors}
            />
        </H>
    );
}
